// @core/stores/i18n.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';

// ============================================
// Types
// ============================================

export type Locale = 'en' | 'ru' | 'zh';

export type TranslationKey = string;
export type TranslationParams = Record<string, string | number>;

export interface Translations {
  [key: string]: string | Translations;
}

// ============================================
// Storage Keys
// ============================================

const STORAGE_KEY = 'app_locale';

// ============================================
// i18n Store
// ============================================

class I18nStore {
  locale: Locale = 'en';
  translations: Map<Locale, Translations> = new Map();
  isLoading = false;
  isInitialized = false;

  private fallbackLocale: Locale = 'en';
  private loadedLocales: Set<Locale> = new Set();

  constructor() {
    makeAutoObservable(this);
  }

  // ============================================
  // Computed Properties
  // ============================================

  get supportedLocales(): Locale[] {
    return ['en', 'ru', 'zh'];
  }

  get currentTranslations(): Translations {
    return this.translations.get(this.locale) ?? {};
  }

  // ============================================
  // Initialization
  // ============================================

  /**
   * Initialize i18n store
   * Loads saved locale from storage and fetches translations
   */
  async initialize(config?: {
    defaultLocale?: Locale;
    loadPath?: string;
  }): Promise<void> {
    if (config?.defaultLocale) {
      this.fallbackLocale = config.defaultLocale;
    }

    // Load saved locale from storage
    this.loadFromStorage();

    // Load translations for current locale
    await this.loadTranslations(this.locale);

    runInAction(() => {
      this.isInitialized = true;
    });
  }

  // ============================================
  // Storage Management
  // ============================================

  private loadFromStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      const savedLocale = localStorage.getItem(STORAGE_KEY) as Locale | null;
      if (savedLocale && this.supportedLocales.includes(savedLocale)) {
        this.locale = savedLocale;
      } else {
        // Try to detect browser locale
        const browserLocale = this.detectBrowserLocale();
        if (browserLocale) {
          this.locale = browserLocale;
        }
      }
    } catch {
      // Use default locale
    }
  }

  private saveToStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(STORAGE_KEY, this.locale);
    } catch {
      // Ignore storage errors
    }
  }

  private detectBrowserLocale(): Locale | null {
    if (typeof navigator === 'undefined') return null;

    const browserLang = navigator.language.split('-')[0];
    if (this.supportedLocales.includes(browserLang as Locale)) {
      return browserLang as Locale;
    }

    return null;
  }

  // ============================================
  // Translation Loading
  // ============================================

  private async loadTranslations(locale: Locale): Promise<void> {
    if (this.loadedLocales.has(locale)) {
      return;
    }

    runInAction(() => {
      this.isLoading = true;
    });

    try {
      const response = await fetch(`/locales/${locale}.json`);
      if (!response.ok) {
        throw new Error(`Failed to load translations for ${locale}`);
      }

      const translations: Translations = await response.json();

      runInAction(() => {
        this.translations.set(locale, translations);
        this.loadedLocales.add(locale);
        this.isLoading = false;
      });
    } catch (error) {
      console.error(`Failed to load translations for ${locale}:`, error);
      runInAction(() => {
        this.isLoading = false;
      });

      // Try to load fallback locale if not already loaded
      if (locale !== this.fallbackLocale && !this.loadedLocales.has(this.fallbackLocale)) {
        await this.loadTranslations(this.fallbackLocale);
      }
    }
  }

  // ============================================
  // Actions
  // ============================================

  /**
   * Change the current locale
   */
  async setLocale(locale: Locale): Promise<void> {
    if (!this.supportedLocales.includes(locale)) {
      console.warn(`Locale ${locale} is not supported`);
      return;
    }

    // Load translations if not already loaded
    await this.loadTranslations(locale);

    runInAction(() => {
      this.locale = locale;
      this.saveToStorage();

      // Update document lang attribute
      if (typeof document !== 'undefined') {
        document.documentElement.lang = locale;
      }
    });
  }

  /**
   * Get a translation by key
   * Supports nested keys like "auth.login.title"
   * Supports parameter interpolation: t("hello", { name: "World" }) -> "Hello, World!"
   */
  t(key: TranslationKey, params?: TranslationParams): string {
    const value = this.getNestedValue(this.currentTranslations, key);

    if (typeof value !== 'string') {
      // Try fallback locale
      if (this.locale !== this.fallbackLocale) {
        const fallbackTranslations = this.translations.get(this.fallbackLocale);
        if (fallbackTranslations) {
          const fallbackValue = this.getNestedValue(fallbackTranslations, key);
          if (typeof fallbackValue === 'string') {
            return this.interpolate(fallbackValue, params);
          }
        }
      }

      // Return key as fallback
      return key;
    }

    return this.interpolate(value, params);
  }

  private getNestedValue(obj: Translations, key: string): string | Translations | undefined {
    const keys = key.split('.');
    let current: string | Translations | undefined = obj;

    for (const k of keys) {
      if (current === undefined || typeof current === 'string') {
        return undefined;
      }
      current = current[k];
    }

    return current;
  }

  private interpolate(template: string, params?: TranslationParams): string {
    if (!params) return template;

    return template.replace(/\{(\w+)\}/g, (_, key) => {
      return params[key]?.toString() ?? `{${key}}`;
    });
  }

  /**
   * Check if a translation key exists
   */
  has(key: TranslationKey): boolean {
    const value = this.getNestedValue(this.currentTranslations, key);
    return typeof value === 'string';
  }

  /**
   * Manually set translations (for testing or SSR)
   */
  setTranslations(locale: Locale, translations: Translations): void {
    runInAction(() => {
      this.translations.set(locale, translations);
      this.loadedLocales.add(locale);
    });
  }
}

// Singleton instance
export const i18nStore = new I18nStore();

// Convenience function for translations
export const t = (key: TranslationKey, params?: TranslationParams): string => {
  return i18nStore.t(key, params);
};
